import {useCallback, useEffect, useRef} from 'react';
import {type NativeScrollEvent, type NativeSyntheticEvent, Platform} from 'react-native';
import {runOnJS, runOnUI, useAnimatedScrollHandler, useSharedValue} from 'react-native-reanimated';
import {useFocusEffect, useIsFocused} from '@react-navigation/native';

import {
  TAB_BAR_HEIGHT,
  useOptionalScrollCollapseContext,
  useOptionalScrollRefContext,
} from '../context/ScrollCollapseContext';

// Minimum delta to register as intentional scroll (prevents sub-pixel jitter loops).
// Android scroll events have more sub-pixel noise than iOS, so a wider dead zone
// reduces unnecessary worklet invocations without affecting perceived responsiveness.
const SCROLL_DEAD_ZONE = Platform.OS === 'android' ? 3 : 1;

function updateScrollValues(
  y: number,
  scrollY: {set: (v: number) => void; get: () => number},
  prevScrollY: {set: (v: number) => void; get: () => number},
  accumulatedUp: {set: (v: number) => void; get: () => number},
  accumulatedDown: {set: (v: number) => void; get: () => number},
  tabBarHeight: number,
) {
  'worklet';
  const clampedY = Math.max(0, y);
  const dy = clampedY - prevScrollY.get();

  // Skip sub-pixel deltas that cause feedback loops near scroll boundaries
  if (Math.abs(dy) < SCROLL_DEAD_ZONE) return;

  scrollY.set(clampedY);

  if (dy > 0) {
    accumulatedDown.set(Math.min(accumulatedDown.get() + dy, tabBarHeight));
    accumulatedUp.set(0);
  } else if (dy < 0) {
    accumulatedUp.set(Math.min(accumulatedUp.get() - dy, tabBarHeight));
    accumulatedDown.set(0);
  }

  prevScrollY.set(clampedY);
}

/**
 * Syncs scroll-collapse shared values with the per-screen scroll position on focus.
 *
 * All screens in the Tab navigator share a single set of shared values (scrollY,
 * prevScrollY, accumulated*). When a screen gains focus, this hook writes
 * `lastScrollY.current` (the per-screen ref updated on every scroll event) into
 * those shared values so the header collapse state matches the actual scroll
 * position of the screen now being displayed.
 *
 * DIS-1127: DO NOT reset `lastScrollY.current` on blur. Tab-navigator screens
 * stay mounted across tab switches (the React component tree persists even when
 * react-native-screens detaches the native view), and the native FlatList's
 * scroll position is preserved across detach/re-attach. Resetting the saved
 * position to 0 on blur would cause the next focus to write ctx.scrollY=0 into
 * the shared header worklet, making the header render at its rest position
 * while the list is actually still scrolled deep — producing a visible header
 * that snaps back to fully-collapsed on the very first user scroll ("header
 * appears mid-screen, disappears on scroll" regression).
 *
 * Stack-popped screens that genuinely need a fresh state unmount their React
 * component entirely, which recreates the `lastScrollY` ref at 0 naturally —
 * no manual cleanup needed.
 */
function useScrollFocusSync(
  lastScrollY: React.MutableRefObject<number>,
  ctx: ReturnType<typeof useOptionalScrollCollapseContext>,
) {
  useFocusEffect(
    useCallback(() => {
      if (!ctx) return;

      const y = lastScrollY.current;
      ctx.scrollY.set(y);
      ctx.prevScrollY.set(y);
      // Set accumulators to match the restored scroll position:
      // - If scrolled past header → keep header collapsed (accumulatedDown = max)
      // - If near top → keep header expanded (accumulatedUp = max)
      // Previously this unconditionally set accumulatedUp = TAB_BAR_HEIGHT,
      // which forced the header to expand even when returning to a deeply-
      // scrolled tab. The headerHeight shared value gives us the threshold.
      const headerH = ctx.headerHeight.get();
      if (y >= headerH) {
        // Scrolled past header — maintain collapsed state
        ctx.accumulatedDown.set(ctx.TAB_BAR_HEIGHT);
        ctx.accumulatedUp.set(0);
      } else {
        // Near top — show header expanded
        ctx.accumulatedUp.set(ctx.TAB_BAR_HEIGHT);
        ctx.accumulatedDown.set(0);
      }
    }, [ctx, lastScrollY]),
  );
}

/**
 * Makes `scrollRef` the scroll-to-top target while this screen is focused.
 *
 * Storing the ref object (not its value) means the button always reads the latest .current, even
 * if the scrollable mounts after this effect fires.
 *
 * On blur, the screen releases the target and zeroes the shared `scrollY`, so a focused screen
 * without a scroll handler (PromotionDetail, Article, Account) does not inherit this screen's
 * offset and show ScrollToTopButton. The release runs only while the target is still this
 * screen's: when the next screen has already registered its own, the next screen's focus write
 * stands. Blur and focus can run in either order. On return, `useScrollFocusSync` restores this
 * screen's offset, so the button shows again.
 */
function useActiveScrollTarget(
  scrollRef: React.RefObject<unknown> | undefined,
  ctx: ReturnType<typeof useOptionalScrollCollapseContext>,
  refCtx: ReturnType<typeof useOptionalScrollRefContext>,
) {
  useFocusEffect(
    useCallback(() => {
      if (!scrollRef || !refCtx) return;
      refCtx.setActiveScrollRef(scrollRef);
      return () => {
        if (refCtx.activeScrollRef.current !== scrollRef) return;
        refCtx.activeScrollRef.current = null;
        ctx?.scrollY.set(0);
      };
    }, [scrollRef, refCtx, ctx]),
  );
}

/**
 * Returns a Reanimated worklet scroll handler for Animated.ScrollView / Animated.FlatList.
 * Pass a scrollRef to register it as the active scrollable for scroll-to-top.
 */
export function useScrollCollapseHandler(scrollRef?: React.RefObject<unknown>) {
  const ctx = useOptionalScrollCollapseContext();
  const refCtx = useOptionalScrollRefContext();

  // Destructure only what the worklet needs — keeps refs out of
  // the worklet closure so mutating .current on tab switch won't trigger
  // "Tried to modify key `current` of an object passed to a worklet".
  const scrollY = ctx?.scrollY;
  const prevScrollY = ctx?.prevScrollY;
  const accumulatedUp = ctx?.accumulatedUp;
  const accumulatedDown = ctx?.accumulatedDown;
  const tabBarHeight = ctx?.TAB_BAR_HEIGHT ?? TAB_BAR_HEIGHT;

  // Per-screen scroll position — survives tab switches on web
  const lastScrollY = useRef(0);
  useScrollFocusSync(lastScrollY, ctx);

  // DIS-1127: Gate the worklet so it only mutates the shared scroll
  // context when THIS screen is the currently-focused one. Without this,
  // a scroll-decelerating screen continues firing onScroll events after
  // the user has tab-switched away, and those worklet writes clobber the
  // newly-focused screen's reset (ctx.scrollY = 0) milliseconds later,
  // leaving the new screen's header rendered in a stale collapsed state
  // ("header appears blank on Cart after navigating mid-scroll from Home").
  const isFocused = useIsFocused();
  const isFocusedSV = useSharedValue(isFocused);
  useEffect(() => {
    isFocusedSV.value = isFocused;
  }, [isFocused, isFocusedSV]);

  useActiveScrollTarget(scrollRef, ctx, refCtx);

  const saveLastY = useCallback((y: number) => {
    lastScrollY.current = y;
  }, []);

  // Reset scroll state when content changes fundamentally (e.g. cart emptied
  // after checkout). Without this, lastScrollY holds the old scroll position
  // from before the content change, and useScrollFocusSync restores it on
  // the next tab focus — collapsing the header even though there's nothing
  // to scroll.
  const resetScrollState = useCallback(() => {
    lastScrollY.current = 0;
    if (ctx) {
      ctx.scrollY.set(0);
      ctx.prevScrollY.set(0);
      ctx.accumulatedUp.set(ctx.TAB_BAR_HEIGHT);
      ctx.accumulatedDown.set(0);
    }
  }, [ctx]);

  const scrollHandler = useAnimatedScrollHandler({
    onScroll: event => {
      'worklet';
      if (!scrollY || !prevScrollY || !accumulatedUp || !accumulatedDown) return;
      const y = event.contentOffset.y;
      // Ignore scroll events from a screen that is no longer focused —
      // e.g. momentum-scroll events that fire after the user has tab-
      // switched away. See DIS-1127 comment on isFocusedSV above.
      if (!isFocusedSV.value) return;
      updateScrollValues(y, scrollY, prevScrollY, accumulatedUp, accumulatedDown, tabBarHeight);
      // Save scroll position for focus restoration (runOnJS is a no-op on web)
      runOnJS(saveLastY)(Math.max(0, y));
    },
  });

  return {scrollHandler, resetScrollState};
}

/**
 * Returns a JS callback scroll handler for non-Reanimated lists (FlashList).
 * Updates shared values via runOnUI to keep animations on the UI thread.
 * Pass a scrollRef to register it as the active scrollable for scroll-to-top.
 */
export function useScrollCollapseJSHandler(scrollRef?: React.RefObject<unknown>) {
  const ctx = useOptionalScrollCollapseContext();
  const refCtx = useOptionalScrollRefContext();

  // Destructure shared values — same reason as useScrollCollapseHandler:
  // keeps refs out of runOnUI closure.
  const scrollY = ctx?.scrollY;
  const prevScrollY = ctx?.prevScrollY;
  const accumulatedUp = ctx?.accumulatedUp;
  const accumulatedDown = ctx?.accumulatedDown;
  const tabBarHeight = ctx?.TAB_BAR_HEIGHT ?? TAB_BAR_HEIGHT;

  // Per-screen scroll position — survives tab switches on web
  const lastScrollY = useRef(0);
  useScrollFocusSync(lastScrollY, ctx);

  // DIS-1127: track focused state so onScroll events from a decelerating
  // screen after tab switch don't overwrite the newly-focused screen's
  // shared scroll state. See useScrollCollapseHandler above.
  const isFocused = useIsFocused();
  const isFocusedRef = useRef(isFocused);
  useEffect(() => {
    isFocusedRef.current = isFocused;
  }, [isFocused]);

  useActiveScrollTarget(scrollRef, ctx, refCtx);

  const jsScrollHandler = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (!scrollY || !prevScrollY || !accumulatedUp || !accumulatedDown) return;
      // Ignore scroll events from a screen that is no longer focused —
      // momentum-scroll events that fire after tab switch would otherwise
      // clobber the newly-focused screen's header-collapse shared state.
      if (!isFocusedRef.current) return;
      const y = event.nativeEvent.contentOffset.y;
      lastScrollY.current = Math.max(0, y);
      runOnUI(updateScrollValues)(
        y,
        scrollY,
        prevScrollY,
        accumulatedUp,
        accumulatedDown,
        tabBarHeight,
      );
    },
    [scrollY, prevScrollY, accumulatedUp, accumulatedDown, tabBarHeight],
  );

  return {scrollHandler: jsScrollHandler};
}

/**
 * Resets the shared scroll-collapse state on focus. Use this for scroll-
 * collapse screens (header has scrollCollapse:true) that do NOT have their
 * own scroll handler to restore a per-screen lastScrollY — e.g.
 * RangeSubDepartmentsScreen, which renders a non-scrolling menu under an
 * animated header. Without this reset, the header reads ctx.scrollY left
 * over from the previously-focused screen (e.g. a scrolled Shop tab) and
 * renders collapsed off-screen, leaving a white header gap.
 */
export function useScrollCollapseReset() {
  const ctx = useOptionalScrollCollapseContext();
  useFocusEffect(
    useCallback(() => {
      if (!ctx) return;
      ctx.scrollY.set(0);
      ctx.prevScrollY.set(0);
      ctx.accumulatedUp.set(ctx.TAB_BAR_HEIGHT);
      ctx.accumulatedDown.set(0);
    }, [ctx]),
  );
}

/**
 * Pins the collapsing header to its expanded (scroll-top) state while `active`
 * is true. Use on scroll-collapse screens whose loading/empty state renders no
 * scrollable — e.g. DiscoverListScreen's masonry skeleton. Without a mounted
 * list to emit onScroll(0), the header reads a stale ctx.scrollY left by another
 * tab (or this tab's persisted position on revisit) and renders collapsed
 * off-screen, leaving a white header gap during loading.
 *
 * Unlike useScrollCollapseReset (focus-only), the focus effect is keyed on
 * `active`, so it also re-fires when `active` flips true *after* focus — the
 * async data → skeleton case. When `active` is false it's a no-op, so the
 * loaded list's own scroll-driven collapse (and per-screen scroll restoration)
 * is left untouched.
 */
export function useScrollCollapseExpandWhile(active: boolean) {
  const ctx = useOptionalScrollCollapseContext();
  useFocusEffect(
    useCallback(() => {
      if (!active || !ctx) return;
      ctx.scrollY.set(0);
      ctx.prevScrollY.set(0);
      ctx.accumulatedUp.set(ctx.TAB_BAR_HEIGHT);
      ctx.accumulatedDown.set(0);
    }, [active, ctx]),
  );
}
